import { memo, useState, useEffect, useCallback, useRef } from 'react';
import style from '@components/checksCommon/checksCart.module.scss';
import NotesIcon from '@assets/images/NotesIcon';
import CardIcon from '@assets/images/CardIcon';
import HoldExpiredMessage from '@components/checksCommon/HoldExpiredMessage';
import panGreen from '@assets/images/panGreen.svg';
import panYellow from '@assets/images/panYellow.svg';
import panOrange from '@assets/images/panOrange.svg';
import { ReactComponent as RefillIcon } from '@assets/images/RefillIcon.svg';
import { HoldTimerIcon } from '@assets/images/HoldIcons/holdTimer.jsx';
import { LABEL } from '@veriables';
import { safeJsonParse, getHoldTimerState } from '@utility/helperFunction';
import styles from '../cart.module.scss';
import { ReactComponent as HoldSending } from '@assets/images/HoldIcons/holdSending.svg';
const EMPTY = [];

const HOLD_RESET = { timeStr: '00:00', isPositive: true };

const HoldTimerText = memo(({ sendDate, serverAppTheme, onFlip }) => {
  const [timerState, setTimerState] = useState(
    () => (sendDate ? getHoldTimerState(sendDate) : HOLD_RESET) || HOLD_RESET
  );
  const prevPositiveRef = useRef(timerState.isPositive);

  useEffect(() => {
    if (!sendDate) return;
    const tick = () => setTimerState(getHoldTimerState(sendDate) || HOLD_RESET);
    tick();
    let intervalId;
    const timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 1000);
    }, 1000 - (Date.now() % 1000));
    return () => { clearTimeout(timeoutId); clearInterval(intervalId); };
  }, [sendDate]);

  useEffect(() => {
    if (timerState.isPositive !== prevPositiveRef.current) {
      prevPositiveRef.current = timerState.isPositive;
      onFlip && onFlip(timerState.isPositive);
    }
  }, [timerState.isPositive, onFlip]);

  const { timeStr } = timerState;
  const color = serverAppTheme?.primary_text || '#000';
  return <span className="fs-12 fw-600 pl-1" style={{ color }}>{timeStr}</span>;
});

const HoldTimerBadge = memo(({ sendDate, serverAppTheme, sendingDate, onFlip }) => {
  return (
    <>
      {!!sendingDate ? (
        <div className="offset-1 pl-10 d-flex align-items-center">
          <span>
            <HoldSending fill={serverAppTheme?.iconcolor} alt="sending" />
          </span>
          <span
            style={{ fontSize: '12px', fontWeight: 500, paddingLeft: '4px' }}
          >
            Sending
          </span>
        </div>
      ) : (
        <div className={styles.holdTimerBadge}>
          <HoldTimerIcon width={14} height={14} fill={serverAppTheme?.primary_text || '#000'} />
          <HoldTimerText
            sendDate={sendDate}
            serverAppTheme={serverAppTheme}
            onFlip={onFlip}
          />
        </div>
      )}
    </>
  );
});

const KDS_ICON = { 2: panYellow, 5: panOrange, 8: panGreen };

const formatLocalTime = timeStamp => {
  if (!timeStamp) return '';
  const date = new Date(timeStamp);
  const newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  const offset = date.getTimezoneOffset() / 60;
  newDate.setHours(date.getHours() - offset);
  let hour = newDate.getHours();
  const minute = newDate.getMinutes();
  let am_pm = 'a';
  if (hour === 0) hour = 12;
  else if (hour > 12) {
    hour -= 12;
    am_pm = 'p';
  } else if (hour === 12) {
    am_pm = 'p';
  }
  const mm = minute < 10 ? `0${minute}` : minute;
  return `${hour}:${mm}${am_pm}`;
};

const Modifiers = ({ mods, arrowColor, nestedColor, nestCategoryTextColor, noteColor, nest = false, displayPrice }) => (
  <div className="m-0 fw-normal">
    {mods.map((m, idx) => (
      <div
        key={`${m.modifierId}-${m.modifierGroupId ?? ''}-${idx}`}
        style={{ color: nest ? nestedColor : 'black', fontSize: nest ? '12px' : '13px' }}
      >
        <span className="fs_18" style={{ color: arrowColor }}>- </span>
        {m.quantity > 1 && (
          <span className="m-0 va-tt">
            {m.quantity} x{' '}
          </span>
        )}
        {m.modifierName}
        {displayPrice && Number(m.price) > 0 && (
          <span className="pl-1 text-muted">
            {`($${Number(m.price).toFixed(2)})`}
          </span>
        )}
        {(m.note || m.comment) && (
          <div
            className="d-flex pt-1"
            style={{ color: noteColor }}
          >
            <span className="pr-1 fs_12">
              <NotesIcon />
            </span>
            <span className="text-break fs_12">{m.note || m.comment}</span>
          </div>
        )}
        {m.modifiers?.length > 0 && (
          <div className="ml-3 mt-1">
            <Modifiers
              mods={m.modifiers}
              arrowColor={arrowColor}
              nestedColor={nestedColor}
              noteColor={noteColor}
              nest
              displayPrice={displayPrice}
            />
          </div>
        )}
      </div>
    ))}
  </div>
);

const CartRow = ({
  item,
  checkSeats,
  actions,
  onAction,
  isChecked,
  onToggle,
  isExpanded,
  onExpand,
  serverAppTheme,
  activeTab,
  displayPrice = true,
  cardType,
  menuItemsHashTable,
  activeCards,
}) => {

  const [isHoldPositive, setIsHoldPositive] = useState(() => {
    return item?.sendDate ? (getHoldTimerState(item.sendDate)?.isPositive ?? true) : true;
  });

  const handleHoldFlip = useCallback((positive) => {
    setIsHoldPositive(positive);
  }, []);

  const isDiscount = !!(item?.isDiscount || item?.discountId || (item?.discountName && !item?.menuItemId));
  const isServiceCharge = !!(item?.isServiceCharge || item?.serviceChargeId || (item?.serviceChargeName && !item?.menuItemId));

  const seatIds =
    item?.seatIds?.length > 0
      ? item.seatIds
      : (item?.seats ?? [])
        .map(s => s?.seatId)
        .filter(id => id != null);

  let seatLabel = '';
  if (seatIds.length > 1) {
    seatLabel = 'S';
  } else if (seatIds.length === 1) {
    const found = checkSeats?.find(s => s?.id === seatIds[0]);
    seatLabel = found?.number ?? '';
  } else if (!isDiscount && !isServiceCharge) {
    seatLabel = 'T';
  }
  const modifiers = (() => {
    if (item?.isLinked) return EMPTY;
    const parsedModJson = safeJsonParse(item?.modifierJson, {});
    if (parsedModJson.isLinked) return EMPTY;
    if (parsedModJson.hasLinkedMenuItem) return parsedModJson.modifiers || EMPTY;
    return item?.modifiers ?? EMPTY;
  })();

  const parsedOrderJson = safeJsonParse(item?.orderJson, null);
  const isRefill = !!parsedOrderJson?.refill;
  const refillMenuItemId = menuItemsHashTable?.[item?.menuItemId]?.refillMenuItemId;
  const refillItem = menuItemsHashTable?.[refillMenuItemId];
  const displayName = item?.serviceChargeName || item?.discountName || (isRefill ? (refillItem?.name || item?.menuItemName) : item?.menuItemName);

  const showKds =
    item?.plannedCompleteDate != null && KDS_ICON[item?.kdsStatus] != null;

  const hasNote = !!(item?.note || item?.comment);
  const validCards = activeCards?.validCards ?? EMPTY;
  const hasInvalidCards = !!activeCards?.hasInvalidCards;
  const isPending = activeTab === 'pending';
  const isHeld = isPending && !!item?.sendDate;
  const hasSendFailureMessage = isHeld && !!item?.message?.trim();
  const hasBottomBlock =
    modifiers.length > 0 ||
    hasNote ||
    showKds ||
    isRefill ||
    validCards.length > 0 ||
    hasInvalidCards ||
    hasSendFailureMessage;

  const isPackItem = menuItemsHashTable?.[item?.menuItemId]?.minOrderQuantity > 0 ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 1 ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 2;

  const visibleActions = (() => {
    if (!actions) return actions;
    if (isDiscount || isServiceCharge) return actions.filter(a => a.key === 'move' || a.key === 'delete' || a.key === 'void');
    if (isPackItem) return actions.filter(a => a.key === 'delete');
    let filtered = actions;
    if (item?.attachToParty) filtered = filtered.filter(a => a.key !== 'edit' && a.key !== 'repeat');
    if (!menuItemsHashTable?.[item?.menuItemId]?.allowRefills || menuItemsHashTable?.[item?.menuItemId]?.isRefill) {
      filtered = filtered.filter(a => a.key !== 'refill');
    }
    return filtered;
  })();

  const isEditDisabled = !!item?.isLinked;

  const isEffectiveHoldPositive =
    !item?.sendingDate &&
    isHoldPositive &&
    (item?.sendDate ? (getHoldTimerState(item.sendDate)?.isPositive ?? true) : true);
  const rowBg = isHeld
    ? { background: isEffectiveHoldPositive ? 'rgb(255, 225, 204)' : 'rgb(245, 176, 176)' }
    : (isDiscount || isServiceCharge)
      ? { background: serverAppTheme?.send_tab_unselected }
      : isPending
        ? { background: serverAppTheme?.pending_items }
        : { background: serverAppTheme?.send_tab_unselected, border: '1px solid transparent' };

  const discountPrice = item?.price ?? item?.value;
  const serviceChargePrice = item?.price ?? item?.amount ?? item?.serviceChargeAmount ?? item?.value ?? 0;
  const showPrice = activeTab === 'sent' || displayPrice;

  return (
    <div
      className="px-0 pb-1"
      style={{ color: serverAppTheme?.primary_text }}
    >
      <div
        className="px-0 roundedSm"
        style={rowBg}
      >
        <div
          role="button"
          onClick={() => onExpand?.(item.id)}
          className={`px-1 pt-2 fw-500 fs_12 ${hasBottomBlock ? '' : 'pb-2'}`}
        >
          <div className="d-flex align-items-start">
            <div className="col-1 px-0 d-flex justify-content-center align-items-center">
              <input
                type="checkbox"
                checked={!!isChecked}
                onChange={() => onToggle?.(item.id)}
                onClick={e => e.stopPropagation()}
                className={style.checkbox}
                style={{
                  border: isChecked ? 'none' : serverAppTheme?.checkbox_border,
                  '--checkbox-checked-color': serverAppTheme?.checkbox_check_color,
                  '--checkbox-checked-bg': serverAppTheme?.checkbox_checked,
                }}
              />
            </div>
            <div className="col-1 px-0 text-center">
              {item?.quantity || ''}
            </div>
            <div className="col-5 px-0 text-left text-break">
              {displayName}
            </div>
            <div className="col-2 px-0 d-flex justify-content-center">
              <div
                className={styles.seatbox}
                style={{
                  background: serverAppTheme?.seat_bg,
                  color: serverAppTheme?.seat_box_text,
                }}
              >
                {seatLabel}
              </div>
            </div>
            <div className="col-3 px-0 pl-1">
              {isDiscount ? (
                showPrice && discountPrice != null && (
                  <>
                    -{LABEL().CURRENCY_SYMBOL}
                    {Math.abs(Number(discountPrice) || 0).toFixed(2)}
                  </>
                )
              ) : isServiceCharge ? (
                showPrice && (
                  <>
                    {LABEL().CURRENCY_SYMBOL}
                    {Number(serviceChargePrice || 0).toFixed(2)}
                  </>
                )
              ) : (
                (activeTab === 'sent' ||
                  (displayPrice && item?.price != null)) && (
                  <>
                    {LABEL().CURRENCY_SYMBOL}
                    {Number(isRefill ? 0 : (item?.price ?? 0)).toFixed(2)}
                  </>
                )
              )}
            </div>
          </div>
          {(item?.note || item?.comment) && (
            <div
              className="d-flex pt-1"
              style={{ color: serverAppTheme?.noteColor }}
            >
              <span className="pr-1">
                <NotesIcon />
              </span>
              <span className="text-break">
                {item.note || item.comment}
              </span>
            </div>
          )}
          {isRefill && (
            <div className="py-2">
              <RefillIcon
                fill={serverAppTheme?.iconcolor}
                width="18"
                height="18"
                aria-label="refill"
              />
            </div>
          )}
          {showKds && (
            <div
              className="d-flex justify-content-start align-items-center pt-1 px-2"
            >
              <div className="text-center" style={{ fontSize: '0.75rem' }}>
                <img
                  src={KDS_ICON[item.kdsStatus]}
                  alt="frying-pan"
                  width="30"
                />
                <div style={{ marginTop: '-2px', fontWeight: 'normal' }}>
                  {formatLocalTime(item.plannedCompleteDate)}
                </div>
              </div>
            </div>
          )}
          {validCards.length > 0 && (
            <div
              className="d-flex align-items-center pt-1 px-2"
              style={{ color: serverAppTheme?.noteColor, fontSize: '0.7rem' }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center mr-2"
                style={{
                  border: '1.5px solid green',
                  backgroundColor: 'rgba(0, 255, 0, 0.08)',
                  borderRadius: '4px',
                  padding: '3px 4px',
                  lineHeight: 0,
                }}
              >
                <CardIcon color="black" />
              </span>
              <span className="text-break">{validCards.join(', ')}</span>
            </div>
          )}
          {hasInvalidCards && (
            <div
              className="d-flex align-items-center pt-1 px-2"
              style={{ color: serverAppTheme?.noteColor }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  border: '1.5px solid red',
                  backgroundColor: 'rgba(255, 0, 0, 0.08)',
                  borderRadius: '4px',
                  padding: '3px 4px',
                  lineHeight: 0,
                }}
              >
                <CardIcon color="black" />
              </span>
            </div>
          )}
          {modifiers.length > 0 && (
            <div className="pt-1">
              <Modifiers
                mods={modifiers}
                arrowColor={serverAppTheme?.modifier_arrowDown}
                nestedColor={serverAppTheme?.primary_text}
                noteColor={serverAppTheme?.noteColor}
                nestCategoryTextColor={serverAppTheme?.Category_text}
                displayPrice={isPending && displayPrice}
              />
            </div>
          )}
          {isHeld && (
            <HoldTimerBadge sendDate={item.sendDate} serverAppTheme={serverAppTheme} sendingDate={item?.sendingDate} onFlip={handleHoldFlip} />
          )}
          {isHeld && (
            <HoldExpiredMessage
              sendDate={item?.sendDate}
              message={item?.message}
              className={styles.holdTimerBadge}
            />
          )}
        </div>

        {isExpanded && visibleActions?.length > 0 && (
          <div className="d-flex flex-wrap px-0 py-1">
            {visibleActions.map(action => {
              const disabled = action.key === 'edit' && isEditDisabled;
              return (
                <div key={action.key} className="col-6 pl-1 pr-0 pb-1">
                  <button
                    type="button"
                    className={styles.rowBtn}
                    style={{
                      width: '100%',
                      background: disabled ? 'rgb(177,177,177)' : (action.bg ?? serverAppTheme?.primaryBtn),
                      color: serverAppTheme?.btn_font_color ?? '#fff',
                      cursor: disabled ? 'not-allowed' : undefined,
                    }}
                    disabled={disabled}
                    onClick={e => {
                      e.stopPropagation();
                      onAction?.(action.key, item);
                    }}
                  >
                    {action.name}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CartRow);
